import React from "react";
import {
  Gauge,
  Radar,
  ShieldCheck,
  Factory,
  Waves,
  BrainCircuit,
} from "lucide-react";
import { motion } from "framer-motion";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { assets } from "../data/siteData";

const features = [
  { title: "Smart Monitoring", icon: Gauge, text: "Live site data" },
  { title: "Smart Surveillance", icon: Radar, text: "Remote alerts" },
  { title: "Smart Remediation", icon: ShieldCheck, text: "Faster response" },
  { title: "Plant Operations", icon: Factory, text: "Centralized control" },
  { title: "Wastewater Management", icon: Waves, text: "Treatment visibility" },
  { title: "AI / ML Analytics", icon: BrainCircuit, text: "Predictive insights" },
];

export default function MonitoringPage() {
  return (
    <>
      <SEO
        title="Smart IoT Water Monitoring Infrastructure in Kolkata, West Bengal | Hydrotec"
        description="Hydrotec Solutions builds IoT-based smart water monitoring infrastructure for Kolkata, Newtown, Rajarhat, and West Bengal. AI-powered analytics, real-time alerts, and connected water management."
        keywords="smart water monitoring Kolkata, IoT water infrastructure West Bengal, water IoT Newtown, water analytics Rajarhat, connected water management Kolkata, water AI West Bengal"
        canonical="https://hydroteclimited.com/monitoring"
      />
      <PageHero
        title="Building Smart Resilient, Reliable & Secure IOT Infrastructure"
        text="AI, IoT and analytics for intelligent water infrastructure."
        image={assets.two}
      />

      {/* HERO VISUAL SECTION */}
      <section className="bg-slate-950 py-14 text-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">

          <div className="flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">
              Intelligent Infrastructure
            </p>

            <h2 className="mt-3 text-3xl font-semibold leading-tight sm:text-4xl">
              Make sense of data.<br />
              See trends.<br />
              Act early.
            </h2>

            <p className="mt-4 text-sm text-slate-300">
              AI + IoT + analytics to improve uptime and decision-making.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {["AI", "IoT", "Predictive"].map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/5 p-3">
            <img
              src={assets.dashboardPreview}
              alt="Dashboard"
              className="h-full w-full rounded-xl object-cover"
            />
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="bg-gradient-to-b from-white to-cyan-50 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.25em] text-emerald-600">
              Capabilities
            </p>

            <h2 className="mt-2 text-3xl font-semibold text-slate-900">
              Smart water infrastructure software
            </h2>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {features.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="group rounded-2xl border border-emerald-100 bg-white/80 backdrop-blur-sm px-5 py-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(16,185,129,0.15)]"
                >
                  {/* Icon */}
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-100 to-emerald-100 text-emerald-600 transition group-hover:scale-110 border-transparent bg-gradient-to-br from-cyan-100 to-emerald-100 p-[1px]">
                    <Icon className="h-5 w-5" />
                  </div>

                  {/* Title */}
                  <h3 className="mt-4 text-lg font-semibold text-slate-900 group-hover:text-emerald-700 transition">
                    {item.title}
                  </h3>

                  {/* Text */}
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {item.text}
                  </p>

                  {/* Bottom accent line */}
                  <div className="mt-4 h-[2px] w-0 bg-gradient-to-r from-cyan-400 to-emerald-500 transition-all duration-300 group-hover:w-full"></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      {/* VISUAL + TEXT SPLIT */}
      <section className="bg-slate-100 py-12">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">

          <div className="overflow-hidden rounded-[1.5rem] shadow-md">
            <img
              src={assets.dashboard4}
              alt="Monitoring"
              className="h-[280px] w-full object-cover"
            />
          </div>

          <div className="flex flex-col justify-center rounded-[1.5rem] bg-slate-900 p-6 text-white">
            <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">
              Why it matters
            </p>

            <h3 className="mt-3 text-2xl font-semibold">
              Predictive, not reactive.
            </h3>

            <p className="mt-3 text-sm text-slate-300">
              Detect issues early and improve uptime using AI insights.
            </p>

            <div className="mt-5 space-y-2">
              <div className="bg-white/5 px-3 py-2 rounded-lg text-sm">
                Make sense of data
              </div>
              <div className="bg-white/5 px-3 py-2 rounded-lg text-sm">
                Understand trends
              </div>
              <div className="bg-white/5 px-3 py-2 rounded-lg text-sm">
                Predict failures early
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SMALL GALLERY */}
      <section className="bg-white py-12">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 sm:px-6 md:grid-cols-3 lg:px-8">

          <img
            src={assets.wat}
            className="h-[220px] w-full rounded-xl object-cover shadow"
          />

          <img
            src={assets.future}
            className="h-[220px] w-full rounded-xl object-cover shadow"
          />

          <div className="flex items-center justify-center rounded-xl bg-slate-900 text-white p-6">
            <div className="text-center">
              <p className="text-xs tracking-[0.25em] text-cyan-300">
                AI + IoT
              </p>
              <h3 className="mt-2 text-xl font-semibold">
                Smarter Operations
              </h3>
              <p className="mt-2 text-sm text-slate-300">
                Real-time + predictive intelligence
              </p>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}